import React, { useState } from 'react';
import { WrenchScrewdriverIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Vehicle, ServiceType } from '../../types/vehicle';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface ServiceRecorderProps {
  vehicle: Vehicle;
  onRecorded?: () => void;
}

const ServiceRecorder: React.FC<ServiceRecorderProps> = ({ vehicle, onRecorded }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    type: 'maintenance' as ServiceType,
    date: new Date().toISOString().split('T')[0],
    mileage: vehicle.mileage || 0,
    description: '',
    cost: '',
    provider: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const serviceData: any = {
        vehicleId: vehicle.id,
        type: formData.type,
        date: new Date(formData.date),
        mileage: parseInt(formData.mileage.toString()),
        description: formData.description,
        provider: formData.provider || undefined,
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        performedBy: user.uid,
        createdAt: serverTimestamp(),
        organizationId: user.organizationId
      };

      // Csak akkor adjuk hozzá a notes-ot, ha van értéke
      if (formData.notes && formData.notes.trim()) {
        serviceData.notes = formData.notes.trim();
      }

      await addDoc(collection(db, 'vehicleServices'), serviceData);

      toast.success('Szerviz rögzítve');
      setIsOpen(false);
      setFormData({
        type: 'maintenance',
        date: new Date().toISOString().split('T')[0],
        mileage: vehicle.mileage || 0,
        description: '',
        cost: '',
        provider: '',
        notes: ''
      });
      if (onRecorded) onRecorded();
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const serviceTypes = [
    { value: 'maintenance', label: 'Karbantartás' },
    { value: 'repair', label: 'Javítás' },
    { value: 'inspection', label: 'Ellenőrzés' },
    { value: 'tire_change', label: 'Gumiabroncs csere' },
    { value: 'oil_change', label: 'Olajcsere' },
    { value: 'other', label: 'Egyéb' }
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        <PlusIcon className="h-4 w-4 mr-2" />
        Szerviz rögzítése
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
        Új szerviz rögzítése
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Szerviz típus */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Szerviz típusa *
            </label>
            <select
              required
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as ServiceType })}
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
            >
              {serviceTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Dátum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Dátum *
            </label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
            />
          </div>

          {/* Km óra */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Km óra állás *
            </label>
            <input
              type="number"
              required
              min="0"
              value={formData.mileage}
              onChange={(e) => setFormData({ ...formData, mileage: parseInt(e.target.value) })}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
            />
          </div>

          {/* Költség */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Költség (HUF)
            </label>
            <input
              type="number"
              min="0"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
              placeholder="0"
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
            />
          </div>
        </div>

        {/* Leírás */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Leírás *
          </label>
          <input
            type="text"
            required
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Pl. Olajcsere és szűrőcsere"
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>

        {/* Szolgáltató */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Szolgáltató
          </label>
          <input
            type="text"
            value={formData.provider}
            onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
            placeholder="Pl. XY Autószerviz"
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>

        {/* Megjegyzés */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Megjegyzés
          </label>
          <textarea
            rows={2}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="További részletek..."
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setFormData({
                type: 'maintenance',
                date: new Date().toISOString().split('T')[0],
                mileage: vehicle.mileage || 0,
                description: '',
                cost: '',
                provider: '',
                notes: ''
              });
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {loading ? 'Mentés...' : 'Rögzítés'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceRecorder;
